/**
 * Per-route social share images.
 *
 * Every public page emits OpenGraph + Twitter Card tags through <PageMeta>.
 * Without this registry every route fell back to a single eBooks card, so a
 * shared /speaking link previewed as a book cover. Routes are matched
 * longest-prefix-first, so /advocacy/pricing inherits the advocacy card unless
 * it declares its own.
 *
 * Images live in public/og/ and are 1200x630 (the size every platform crops to
 * for `summary_large_image`). Add a new entry here rather than passing
 * `image=` at the call site so the mapping stays auditable in one place.
 */

export interface OgImage {
  /** Site-relative path under public/. Resolved to absolute in PageMeta. */
  src: string;
  /** Alt text shown by screen readers on platforms that surface it. */
  alt: string;
}

export const DEFAULT_OG_IMAGE: OgImage = {
  src: "/og/default.jpg",
  alt: "Dr. Sully Speaks — MAKE CHANGE: faith-integrated leadership and advocacy",
};

const SPEAKING: OgImage = {
  src: "/og/speaking.jpg",
  alt: "Keynote speaking with Dr. Sully — PsyD, ThD, DMin, CERC",
};

const ADVOCACY: OgImage = {
  src: "/og/advocacy.jpg",
  alt: "Full-Scope Advocacy Partner and consulting with Dr. Sully, San Antonio, Texas",
};

const STORIES: OgImage = {
  src: "/og/stories.jpg",
  alt: "Stories and insights from Dr. Sully Speaks",
};

const EBOOKS: OgImage = {
  src: "/og/ebooks.jpg",
  alt: "eBooks and information products by Dr. Sully",
};

const MASTERCLASS: OgImage = {
  src: "/og/masterclass.jpg",
  alt: "The free MAKE CHANGE masterclass with Dr. Sully",
};

/** Longest matching prefix wins; "/" is the sitewide fallback. */
const ROUTE_OG_IMAGES: Record<string, OgImage> = {
  "/": DEFAULT_OG_IMAGE,

  // Speaking + platform authority
  "/speaking": SPEAKING,
  "/reviews": SPEAKING,
  "/links": SPEAKING,
  "/press": SPEAKING,
  "/pr-template": SPEAKING,

  // Advocacy, consulting, coaching, and the money pages
  "/advocacy": ADVOCACY,
  "/consulting": ADVOCACY,
  "/coaching": ADVOCACY,
  "/book": ADVOCACY,
  "/invest": ADVOCACY,
  "/inquire": ADVOCACY,
  "/strategy": ADVOCACY,
  "/strategy-session": ADVOCACY,
  "/funnel": ADVOCACY,
  "/compassion": ADVOCACY,

  // Written + editorial content
  "/stories": STORIES,
  "/share-story": STORIES,
  "/testimonial": STORIES,
  "/blog": STORIES,
  "/insights": STORIES,
  "/about": STORIES,
  "/framework": STORIES,
  "/honest-kingdom-voice": STORIES,

  // Products
  "/ebooks": EBOOKS,
  "/books": EBOOKS,
  "/resources": EBOOKS,
  "/free": EBOOKS,

  // Lead magnets
  "/masterclass": MASTERCLASS,
  "/scan": MASTERCLASS,
  "/cerc": MASTERCLASS,
};

/**
 * Resolve the share card for a route path. Query strings, hashes, trailing
 * slashes and casing are ignored so /Speaking/?utm=x maps like /speaking.
 */
export const resolveOgImage = (path: string): OgImage => {
  const clean = path.split("?")[0].split("#")[0].toLowerCase();
  const normalized =
    clean.length > 1 && clean.endsWith("/") ? clean.slice(0, -1) : clean || "/";

  if (ROUTE_OG_IMAGES[normalized]) return ROUTE_OG_IMAGES[normalized];

  const match = Object.keys(ROUTE_OG_IMAGES)
    .filter((route) => route !== "/" && normalized.startsWith(`${route}/`))
    .sort((a, b) => b.length - a.length)[0];

  return match ? ROUTE_OG_IMAGES[match] : DEFAULT_OG_IMAGE;
};
